import PillarTableFilterCheckbox from "shared/components/pillar-table/filters/PillarTableFilterCheckbox";
import PillarTableFilterDatePicker from "shared/components/pillar-table/filters/PillarTableFilterDatePicker";
import PillarTableFilterSelectMenu from "shared/components/pillar-table/filters/PillarTableFilterSelectMenu";
import PillarTableFilterTextInput from "shared/components/pillar-table/filters/PillarTableFilterTextInput";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import { useSocietySession } from "shared/context/SocietySessionContext";
import {
  type AppFilterOption,
  FilterComponentType,
} from "shared/filter-where-clause";
import { base_table } from "shared/prisma/base-table";

const PillarTableFiltersCustomReport = () => {
  const { customReportResponse, setFilters } = usePillarTableContext();
  const filters = customReportResponse?.filtersConfig ?? [];

  // Raw-query reports have no filters (non-admins don't get their SQL)
  if (
    filters.length === 0 ||
    customReportResponse?.baseTable === base_table.raw_query
  ) {
    return null;
  }

  return (
    <div className="w-full flex justify-between py-1">
      <div className="mb-1 flex justify-start items-end w-full gap-x-1 gap-y-1 flex-wrap">
        {filters.map((item) => {
          const type = item.component?.type as FilterComponentType;
          const Component = FilterComponentMap[type];
          if (!Component) {
            return null;
          }

          // spread EVERY field of item into the Component
          return <Component key={item.filterKey} {...item} />;
        })}
        <button
          data-testid="clear-filters-button"
          type="button"
          className="p-1 h-4.75 border rounded border-neutral-mid-200 text-filter-label mr-0.25 whitespace-nowrap"
          onClick={() => setFilters({})}
        >
          Clear Filters
        </button>
      </div>
    </div>
  );
};

export default PillarTableFiltersCustomReport;
type FilterComponentMap = {
  [K in FilterComponentType]?: React.FC<AppFilterOption>;
};

const FilterComponentMap: FilterComponentMap = {
  [FilterComponentType.none]: () => null,

  [FilterComponentType.search]: (opt) => {
    const { filterKey, component } = opt;
    // if missing, nothing to render
    if (!filterKey || !component?.label) {
      return null;
    }

    return (
      <PillarTableFilterTextInput
        filterKey={filterKey}
        testId={filterKey}
        title={component.label}
        toolTip={component.toolTip}
        className="w-full min-w-4xl"
      />
    );
  },

  [FilterComponentType.Date]: (opt) => {
    const { filterKey, component } = opt;
    const society = useSocietySession();
    if (!filterKey || !component?.label) {
      return null;
    }

    return (
      <div className="flex flex-col min-w-6xl">
        <PillarTableFilterDatePicker
          filterKey={filterKey}
          title={component.label}
          toolTip={component.toolTip}
          society={society}
        />
      </div>
    );
  },

  [FilterComponentType.selectMenuCheckbox]: (opt) => {
    const { filterKey, component = [] } = opt;
    if (
      !filterKey ||
      !component ||
      !("label" in component) ||
      !component.label
    ) {
      return null;
    }

    return (
      <PillarTableFilterSelectMenu
        isMultiple
        filterKey={filterKey}
        title={component.label}
        toolTip={"toolTip" in component ? component.toolTip : undefined}
        choices={"choices" in component ? component.choices : undefined}
        defaultValue={
          "defaultValue" in component
            ? (component.defaultValue as string[])
            : undefined
        }
        className="min-w-[160px] block pillartable-filters-selectmenu-dropdown-container"
      />
    );
  },

  [FilterComponentType.selectMenu]: (opt) => {
    const { filterKey, component } = opt;
    if (
      !filterKey ||
      !component ||
      !("label" in component) ||
      !component.label ||
      !("choices" in component) ||
      !component.choices ||
      !Array.isArray(component.choices)
    ) {
      return null;
    }

    return (
      <PillarTableFilterSelectMenu
        isMultiple={false}
        filterKey={filterKey}
        title={component.label}
        toolTip={component.toolTip}
        selectOptionQuery={async () => {
          return [
            ...component.choices!.map((choice) => ({
              value: choice.value,
              display: choice.display,
            })),
          ];
        }}
        defaultValue={component.defaultValue as string | undefined}
        className="min-w-[160px] block pillartable-filters-selectmenu-dropdown-container"
      />
    );
  },

  [FilterComponentType.checkbox]: (opt) => {
    const { filterKey, component } = opt;
    if (!filterKey || !component?.label) {
      return null;
    }

    return (
      <PillarTableFilterCheckbox
        filterKey={filterKey}
        title={component.label}
        toolTip={component.toolTip}
        isRowStyle
      />
    );
  },
};
